/**
 * @file legal-targets-store.ts
 * @description Общий на вкладку список документов и групп магазина
 * (phase19.16, партия «Юридические документы»): один запрос на все места,
 * которым он нужен одновременно — поле цели пункта меню, строки списка меню
 * («Документ» / «Группа документов»), окно и строки «Ссылок» подвала.
 *
 * Хранилище без запросов (их делает `api-legal-targets`): так его может
 * звать `api-legal.ts` после любой правки документа без цикла импортов.
 * Ответ привязан к кабинету вкладки (`key` — id контекста): смена магазина
 * (AuthProvider) и правки документов сбрасывают список, подписчики
 * перечитывают его с сервера — один источник правды.
 */

import type { LegalTarget } from "../api-legal-targets";

let cached: { key: string; promise: Promise<LegalTarget[]> } | null = null;
const listeners = new Set<() => void>();

/** Запомненный ответ для кабинета `key`; другой кабинет — нет. */
export function cachedLegalTargets(key: string): Promise<LegalTarget[]> | null {
  return cached?.key === key ? cached.promise : null;
}

/** Запомнить запрос; ошибка не запоминается — следующий спросит снова. */
export function rememberLegalTargets(key: string, promise: Promise<LegalTarget[]>): Promise<LegalTarget[]> {
  const entry = { key, promise };
  cached = entry;
  promise.catch(() => {
    if (cached === entry) cached = null;
  });
  return promise;
}

/** Сбросить список и сообщить подписчикам: они перечитают его. */
export function invalidateLegalTargets(): void {
  cached = null;
  for (const listener of listeners) listener();
}

/** Подписка на сброс; результат — отписка. */
export function subscribeLegalTargets(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Сброс без уведомления (тесты: каждый тест со своим ответом сервера). */
export function resetLegalTargetsStore(): void {
  cached = null;
  listeners.clear();
}
